import { omit } from 'lodash-es';
import { suffixClientId } from './suffix-editor';
import { EMPTY_CELL } from 'utils/empty-cell';
import type {
  AutoMatchConfiguration,
  AutoMatchConfigurationInput,
  AutoMatchDecisionRow,
  AutoMatchOutcome,
  EntityFormSuffix,
  GuardrailMeta,
} from './types';

export const OUTCOME_LABEL: Record<AutoMatchOutcome, string> = {
  auto_confirmed: 'Auto-confirmed',
  manual_review: 'Manual review',
};

export const OUTCOME_TAG_TYPE: Record<AutoMatchOutcome, 'green' | 'gray'> = {
  auto_confirmed: 'green',
  manual_review: 'gray',
};

// The hold for a briefed run whose LLM gave no usable pick, which the gate
// evaluates before Threshold and the guardrails.
export const LLM_REASON_LABEL: Record<string, string> = {
  LLM: 'LLM — no usable pick',
};

// Suppression reasons that are not guardrail ids (the gate's prose reasons).
// Guardrail ids and Threshold read as they are.
export const SUPPRESSION_REASON_LABEL: Record<string, string> = {
  no_candidates: 'No candidates',
  county_not_enabled: 'County not enabled',
  auto_match_disabled: 'Auto-match disabled',
  merge_unresolved: 'Merge unresolved',
};

/**
 * Every suppression reason in words: the prose reasons, and each guardrail as
 * "<id> — <name>" from the config's roster. Threshold reads as itself. The one
 * map the reason filter, column, detail and stats use.
 * @param {GuardrailMeta[]} guardrails - The config's guardrail roster
 * @return {Record<string, string>} Labels by token
 */
export const reasonLabels = (
  guardrails: GuardrailMeta[] = []
): Record<string, string> => ({
  ...LLM_REASON_LABEL,
  ...SUPPRESSION_REASON_LABEL,
  ...Object.fromEntries(
    guardrails.map((guardrail) => [
      guardrail.id,
      `${guardrail.id} — ${guardrail.name}`,
    ])
  ),
});

/**
 * The basis for an auto-confirm, as the server names it, or the suppression
 * reason for a manual review.
 * @param decision - The decision's outcome, basis label and suppression reason
 * @param {Record<string, string>} labels - The reason labels
 * @return {string} The label, or the em dash
 */
export const reasonLabel = (
  decision: Pick<
    AutoMatchDecisionRow,
    'outcome' | 'basisLabel' | 'suppressionReason'
  >,
  labels: Record<string, string>
): string => {
  if (decision.outcome === 'auto_confirmed') {
    return decision.basisLabel ?? EMPTY_CELL;
  }
  const reason = decision.suppressionReason;
  return reason ? (labels[reason] ?? reason) : EMPTY_CELL;
};

export interface FormState {
  enabled: boolean;
  thresholdT: number;
  thresholdDelta: number;
  llmConfidenceThreshold: number;
  infoSuppresses: boolean;
  guardrailsEnabled: Record<string, boolean>;
  enabledCountyIds: number[];
  organizationalTokensText: string;
  neverAutoPhrasesText: string;
  entityFormSuffixes: EntityFormSuffix[];
  rationale: string;
}

/**
 * Comma- (or newline-) separated text as a trimmed, case-insensitively deduped
 * list.
 * @param {string} text - The text
 * @return {string[]} The list
 */
export const parseList = (text: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  text.split(/[,\n]/).forEach((raw) => {
    const value = raw.trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
};

/**
 * The editable form for a saved configuration.
 * @param {AutoMatchConfiguration} config - The saved configuration
 * @return {FormState} The form state
 */
export function seed(config: AutoMatchConfiguration): FormState {
  return {
    enabled: config.enabled,
    thresholdT: config.thresholdT,
    thresholdDelta: config.thresholdDelta,
    llmConfidenceThreshold: config.llmConfidenceThreshold,
    infoSuppresses: config.infoSuppresses,
    guardrailsEnabled: { ...config.guardrailsEnabled },
    enabledCountyIds: [...config.enabledCountyIds],
    organizationalTokensText: config.organizationalTokens.join(', '),
    neverAutoPhrasesText: config.neverAutoPhrases.join(', '),
    entityFormSuffixes: config.entityFormSuffixes.map((s) => ({
      ...s,
      // A stable client-only id per row, assigned on hydration so the editor's
      // React key survives a middle-row removal. Stripped again on save.
      clientId: suffixClientId(),
      variants: [...s.variants],
      organizationTypes: [...(s.organizationTypes ?? [])],
    })),
    rationale: '',
  };
}

/**
 * The parsed payload a save sends (minus rationale) — also the basis of the
 * dirty check, so formatting-only edits ("a,b" → "a, b") stay clean.
 * @param {FormState} form - The form
 * @return The payload without the rationale
 */
export const buildInput = (
  form: FormState
): Omit<AutoMatchConfigurationInput, 'rationale'> => ({
  enabled: form.enabled,
  thresholdT: form.thresholdT,
  thresholdDelta: form.thresholdDelta,
  llmConfidenceThreshold: form.llmConfidenceThreshold,
  infoSuppresses: form.infoSuppresses,
  // Sorted so the dirty check is order-insensitive — a deselect→reselect that
  // only reorders the Set never reads as a change.
  enabledCountyIds: [...form.enabledCountyIds].sort((a, b) => a - b),
  guardrailsEnabled: form.guardrailsEnabled,
  organizationalTokens: parseList(form.organizationalTokensText),
  // clientId is a client-only row key — strip it so it is neither posted nor
  // counted in the dirty check.
  entityFormSuffixes: form.entityFormSuffixes
    .filter((s) => s.canonical.trim())
    .map((s) => omit(s, 'clientId')),
  neverAutoPhrases: parseList(form.neverAutoPhrasesText),
});

// The upper bound of each editable threshold; every one starts at 0 and is a
// whole number, as the server requires.
export const THRESHOLD_MAX = {
  thresholdT: 100,
  thresholdDelta: 30,
  llmConfidenceThreshold: 100,
} as const;

export type ThresholdField = keyof typeof THRESHOLD_MAX;

/**
 * Whether a threshold is a whole number within its range.
 * @param {FormState} form - The form
 * @param {ThresholdField} field - The threshold
 * @return {boolean} Whether the server accepts it
 */
const thresholdValid = (form: FormState, field: ThresholdField): boolean => {
  const value = form[field];
  return Number.isInteger(value) && value >= 0 && value <= THRESHOLD_MAX[field];
};

/**
 * Whether each editable threshold is a value the server accepts.
 * @param {FormState} form - The form
 * @return {Record<ThresholdField, boolean>} One flag per threshold
 */
export const thresholdValidity = (
  form: FormState
): Record<ThresholdField, boolean> => ({
  thresholdT: thresholdValid(form, 'thresholdT'),
  thresholdDelta: thresholdValid(form, 'thresholdDelta'),
  llmConfidenceThreshold: thresholdValid(form, 'llmConfidenceThreshold'),
});

/**
 * A number input's value as a number; a cleared input is not a number.
 * @param {string | number | undefined} value - The input's value
 * @return {number} The number, or NaN when blank
 */
export const numberOf = (value: string | number | undefined): number =>
  value === undefined || value === '' ? Number.NaN : Number(value);
